import { useMemo } from 'react'

import {
  digObject, matchFilterArrayIncludes, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

const watchEntityKeys = ['services']

export type ServicesFilters = {
  assigned_provider_id?: number,
  filterTemplatable?: boolean,
  service_id?: number,
  string?: string,
}

interface UseServicesOptions {
  entityKey?: string,
  filters?: ServicesFilters,
  performHttpRequests?: boolean,
  requestOptions?: object,
}

function useServices(options: UseServicesOptions = {}) {
  const { filters = {}, requestOptions } = options
  const {
    assigned_provider_id: filterAssignedProviderId,
    filterTemplatable,
    service_id: filterServiceId,
    string: filterString,
  } = filters

  const {
    updatedEntities: { services: servicesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const services = useSelector(reduxState => reduxState.entities.services)

  const filteredServices = useMemo(() => {
    const filtered = Object.values(services).filter((service) => {
      const {
        assigned_provider_ids = [],
        id,
        title,
      } = service

      const matchAssignedProvider = matchFilterArrayIncludes(assigned_provider_ids, filterAssignedProviderId)
      const matchId = id.toString() === filterString
      const matchServiceId = matchFilterNumber(id, filterServiceId)
      const matchTemplatable = filterTemplatable ? digObject(service, 'options.templatable', false) : true
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      return matchAssignedProvider && matchServiceId && matchTemplatable && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [servicesUpdatedAt, JSON.stringify(filters)])

  const filteredServicesCount = filteredServices.length
  const hasFilteredServices = !!filteredServicesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredServicesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingServices } = useReduxAction(
    'services',
    'loadServices',
    {
      ...filtersWithOffset,
      ...requestOptions,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: () => performHttpRequests,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredServices,
    filteredServicesCount,
    hasFilteredServices,
    loading: loadingServices,
    services,
  }
}

export default useServices
